import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  Legend, LineChart, Line,
} from "recharts";
import { Download, Target, FileDown } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/Common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";


const SUPPORTED_CURRENCIES = ["USD", "IDR", "AUD"];

// Module-level currency state, lifted via a tiny context-less event so each
// tab's query refetches automatically when the user changes the dropdown.
let _currentCurrency = "USD";
const _currencyListeners = new Set();
function getCurrency() { return _currentCurrency; }
function setCurrency(v) {
  _currentCurrency = v;
  _currencyListeners.forEach((cb) => cb(v));
}
function useCurrency() {
  const [c, setC] = useState(_currentCurrency);
  useEffect(() => {
    const cb = (v) => setC(v);
    _currencyListeners.add(cb);
    return () => _currencyListeners.delete(cb);
  }, []);
  return c;
}


// ────────────────────────────────────────────────────────────────────────────
// CSV export helper — runs entirely client-side. We never trust user data for
// HTML/SQL; CSV escaping is sufficient here.
// ────────────────────────────────────────────────────────────────────────────
function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => {
      const v = r[h] ?? "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}


const CHART_COLORS = {
  total: "#C8102E",
  paid: "#16A34A",
  outstanding: "#0A0A0A",
  tax: "#F59E0B",
};


function YearPicker({ year, setYear, testId = "report-year" }) {
  const current = new Date().getFullYear();
  return (
    <div className="flex items-center gap-2">
      <Label className="overline mb-0">Year</Label>
      <Input
        data-testid={testId}
        type="number" min={current - 10} max={current + 2}
        value={year} onChange={(e) => setYear(Number(e.target.value || current))}
        className="w-24"
      />
    </div>
  );
}


function MonthlyReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const currency = useCurrency();
  const { data, isLoading } = useQuery({
    queryKey: ["report", "month", year, currency],
    queryFn: () => api.reportSalesByMonth(year, currency),
  });
  const rows = data?.rows || [];
  const totalRevenue = rows.reduce((s, r) => s + (r.total || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + (r.paid || 0), 0);
  const totalOutstanding = rows.reduce((s, r) => s + (r.outstanding || 0), 0);
  return (
    <div data-testid="report-monthly">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <YearPicker year={year} setYear={setYear} testId="report-month-year" />
        <div className="flex gap-2">
          <Button data-testid="report-month-pdf" variant="outline"
                  onClick={() => api.exportReportPdf({
                    title: `Monthly Sales — ${year}`,
                    subtitle: `Currency: ${currency}`,
                    columns: ["Month", "Invoices", "Subtotal", "Tax", "Total", "Paid", "Outstanding"],
                    rows: rows.map((r) => [r.label, r.invoices, fmtMoney(r.subtotal), fmtMoney(r.tax), fmtMoney(r.total), fmtMoney(r.paid), fmtMoney(r.outstanding)]),
                  }, `sales-monthly-${year}.pdf`)}>
            <FileDown className="size-3.5 mr-1.5" /> PDF
          </Button>
          <Button data-testid="report-month-csv" variant="outline"
                  onClick={() => downloadCSV(`sales-monthly-${year}.csv`, rows)}>
            <Download className="size-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <KpiCard label="Total revenue" value={`${fmtMoney(totalRevenue)} ${currency}`} color="text-primary" />
        <KpiCard label="Total paid" value={`${fmtMoney(totalPaid)} ${currency}`} color="text-emerald-700" />
        <KpiCard label="Total outstanding" value={`${fmtMoney(totalOutstanding)} ${currency}`} color="text-rose-700" />
      </div>
      <div className="border border-border rounded-md bg-white p-3 mb-4" style={{ height: 320 }}>
        {isLoading ? <Skeleton /> : (
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="total" name="Total" fill={CHART_COLORS.total} />
              <Bar dataKey="paid" name="Paid" fill={CHART_COLORS.paid} />
              <Bar dataKey="outstanding" name="Outstanding" fill={CHART_COLORS.outstanding} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <ReportTable
        headers={["Month", "Invoices", "Subtotal", "Tax", "Total", "Paid", "Outstanding"]}
        rows={rows.map((r) => [r.label, r.invoices, fmtMoney(r.subtotal), fmtMoney(r.tax), fmtMoney(r.total), fmtMoney(r.paid), fmtMoney(r.outstanding)])}
        testId="report-month-table"
      />
    </div>
  );
}


function QuarterlyReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const currency = useCurrency();
  const { data, isLoading } = useQuery({
    queryKey: ["report", "quarter", year, currency],
    queryFn: () => api.reportSalesByQuarter(year, currency),
  });
  const rows = data?.rows || [];
  return (
    <div data-testid="report-quarterly">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <YearPicker year={year} setYear={setYear} testId="report-quarter-year" />
        <div className="flex gap-2">
          <Button data-testid="report-quarter-pdf" variant="outline"
                  onClick={() => api.exportReportPdf({
                    title: `Quarterly Sales — ${year}`,
                    subtitle: `Currency: ${currency}`,
                    columns: ["Quarter", "Invoices", "Subtotal", "Tax", "Total", "Paid", "Outstanding"],
                    rows: rows.map((r) => [r.label, r.invoices, fmtMoney(r.subtotal), fmtMoney(r.tax), fmtMoney(r.total), fmtMoney(r.paid), fmtMoney(r.outstanding)]),
                  }, `sales-quarterly-${year}.pdf`)}>
            <FileDown className="size-3.5 mr-1.5" /> PDF
          </Button>
          <Button data-testid="report-quarter-csv" variant="outline"
                  onClick={() => downloadCSV(`sales-quarterly-${year}.csv`, rows)}>
            <Download className="size-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>
      <div className="border border-border rounded-md bg-white p-3 mb-4" style={{ height: 320 }}>
        {isLoading ? <Skeleton /> : (
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="total" name="Total" fill={CHART_COLORS.total} />
              <Bar dataKey="paid" name="Paid" fill={CHART_COLORS.paid} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <ReportTable
        headers={["Quarter", "Invoices", "Subtotal", "Tax", "Total", "Paid", "Outstanding"]}
        rows={rows.map((r) => [r.label, r.invoices, fmtMoney(r.subtotal), fmtMoney(r.tax), fmtMoney(r.total), fmtMoney(r.paid), fmtMoney(r.outstanding)])}
        testId="report-quarter-table"
      />
    </div>
  );
}


function ProfitLossReport() {
  const currency = useCurrency();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, isLoading } = useQuery({
    queryKey: ["report", "pnl", year, currency],
    queryFn: () => api.reportProfitLoss(year, currency),
  });
  const rows = data?.rows || [];
  const totals = data?.totals || { revenue: 0, overhead: 0, net_profit: 0, invoiced: 0, billable_expenses: 0 };
  const margin = totals.revenue > 0
    ? ((totals.net_profit / totals.revenue) * 100).toFixed(1)
    : "0.0";

  return (
    <div data-testid="report-pnl">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-xs text-muted-foreground">
            Cash-basis P&amp;L. Revenue = payments received. Overhead = non-billable expenses.
            Net profit = Revenue − Overhead.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <YearPicker year={year} setYear={setYear} testId="pnl-year" />
          <Button
            data-testid="pnl-pdf"
            variant="outline"
            size="sm"
            onClick={() => api.exportReportPdf({
              title: `Profit & Loss — ${year}`,
              subtitle: `Cash-basis · Currency: ${currency}`,
              columns: ["Month", "Revenue", "Overhead", "Net profit", "Invoiced", "Billable expenses"],
              rows: rows.map((r) => [r.label, fmtMoney(r.revenue), fmtMoney(r.overhead), fmtMoney(r.net_profit), fmtMoney(r.invoiced), fmtMoney(r.billable_expenses)]),
            }, `profit-loss-${year}-${currency}.pdf`)}
          >
            <FileDown className="size-3.5 mr-1" /> PDF
          </Button>
          <Button
            data-testid="pnl-csv"
            variant="outline"
            size="sm"
            onClick={() =>
              downloadCSV(`profit-loss-${year}-${currency}.csv`,
                rows.map((r) => ({
                  Month: r.label,
                  Revenue: r.revenue,
                  Overhead: r.overhead,
                  "Net profit": r.net_profit,
                  Invoiced: r.invoiced,
                  "Billable expenses": r.billable_expenses,
                }))
              )
            }
          >
            <Download className="size-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* KPI ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <PnlKpi label="Revenue" value={totals.revenue} currency={currency} tone="emerald" testid="pnl-kpi-revenue" />
        <PnlKpi label="Overhead" value={totals.overhead} currency={currency} tone="red" testid="pnl-kpi-overhead" />
        <PnlKpi
          label="Net profit"
          value={totals.net_profit}
          currency={currency}
          tone={totals.net_profit >= 0 ? "emerald" : "red"}
          testid="pnl-kpi-net"
        />
        <PnlKpi
          label={`Margin (${margin}%)`}
          value={totals.invoiced}
          valueLabel="Invoiced"
          currency={currency}
          tone="slate"
          testid="pnl-kpi-margin"
        />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <>
          <div className="border border-border rounded-md bg-white p-4 mb-5" data-testid="pnl-chart-wrap">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                />
                <Tooltip
                  formatter={(v, name) => [`${fmtMoney(v)} ${currency}`, name]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#16A34A" radius={[3, 3, 0, 0]} />
                <Bar dataKey="overhead" name="Overhead" fill="#C8102E" radius={[3, 3, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="net_profit"
                  name="Net profit"
                  stroke="#0A0A0A"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="border border-border rounded-md bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border">
                  <TableHead className="overline">Month</TableHead>
                  <TableHead className="overline text-right">Invoiced</TableHead>
                  <TableHead className="overline text-right">Revenue</TableHead>
                  <TableHead className="overline text-right">Overhead</TableHead>
                  <TableHead className="overline text-right">Billable exp.</TableHead>
                  <TableHead className="overline text-right">Net profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.month} data-testid={`pnl-row-${r.month}`}>
                    <TableCell className="font-medium">{r.label}</TableCell>
                    <TableCell className="text-right mono text-muted-foreground">{fmtMoney(r.invoiced)}</TableCell>
                    <TableCell className="text-right mono text-emerald-700">{fmtMoney(r.revenue)}</TableCell>
                    <TableCell className="text-right mono text-red-700">{fmtMoney(r.overhead)}</TableCell>
                    <TableCell className="text-right mono text-muted-foreground">{fmtMoney(r.billable_expenses)}</TableCell>
                    <TableCell className={`text-right mono font-semibold ${r.net_profit >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                      {fmtMoney(r.net_profit)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50 font-semibold" data-testid="pnl-row-total">
                  <TableCell>Total {year}</TableCell>
                  <TableCell className="text-right mono">{fmtMoney(totals.invoiced)}</TableCell>
                  <TableCell className="text-right mono text-emerald-800">{fmtMoney(totals.revenue)}</TableCell>
                  <TableCell className="text-right mono text-red-800">{fmtMoney(totals.overhead)}</TableCell>
                  <TableCell className="text-right mono">{fmtMoney(totals.billable_expenses)}</TableCell>
                  <TableCell className={`text-right mono ${totals.net_profit >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                    {fmtMoney(totals.net_profit)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

function PnlKpi({ label, value, valueLabel, currency, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-white border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    red: "bg-red-50 border-red-200",
  };
  const valueTone = {
    slate: "text-slate-900",
    emerald: "text-emerald-800",
    red: "text-red-800",
  };
  return (
    <div data-testid={testid} className={`rounded-md border p-3 ${toneMap[tone]}`}>
      <p className="overline">{valueLabel ? valueLabel : label}</p>
      <p className={`text-xl font-bold font-mono mt-1 ${valueTone[tone]}`}>
        {fmtMoney(value)} <span className="text-xs text-muted-foreground">{currency}</span>
      </p>
      {valueLabel && (
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
      )}
    </div>
  );
}



// ────────────────────────────────────────────────────────────────────────────
// Project Profitability — per-invoice revenue, COGS (traced from the linked
// quotation's markup lines), linked expenses and net profit.
// ────────────────────────────────────────────────────────────────────────────
function ProjectProfitabilityReport() {
  const currency = useCurrency();
  const [year, setYear] = useState(new Date().getFullYear());
  const [onlyWithCost, setOnlyWithCost] = useState(false);
  const [groupBy, setGroupBy] = useState("invoice"); // "invoice" | "client"
  const { data, isLoading } = useQuery({
    queryKey: ["report", "project-profit", year, currency],
    queryFn: () => api.reportProjectProfitability(year, currency),
  });
  const allRows = data?.rows || [];
  const rows = onlyWithCost ? allRows.filter((r) => r.has_cost_data) : allRows;
  const totals = data?.totals || {
    revenue: 0, cogs: 0, linked_expenses: 0,
    billable_expenses: 0, gross_margin: 0, net_profit: 0, margin_pct: 0,
  };

  // Client-level rollup — computed from the same base rows so the toggle is
  // free (no extra API call). Sorted by net profit descending.
  const clientRows = (() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.client_name || "—";
      if (!map.has(key)) {
        map.set(key, {
          client_name: key,
          invoices: 0,
          revenue: 0, cogs: 0, linked_expenses: 0,
          gross_margin: 0, net_profit: 0,
          has_cost_data_count: 0,
        });
      }
      const g = map.get(key);
      g.invoices += 1;
      g.revenue += r.revenue;
      g.cogs += r.cogs;
      g.linked_expenses += r.linked_expenses;
      g.gross_margin += r.gross_margin;
      g.net_profit += r.net_profit;
      if (r.has_cost_data) g.has_cost_data_count += 1;
    }
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        margin_pct: g.revenue > 0 ? Math.round((g.net_profit / g.revenue) * 10000) / 100 : 0,
      }))
      .sort((a, b) => b.net_profit - a.net_profit);
  })();

  return (
    <div data-testid="report-project-profitability">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="max-w-2xl">
          <p className="text-xs text-muted-foreground">
            Per-project profitability. <strong>Cost</strong> is traced back
            through each invoice&apos;s source: Invoice → Delivery Order →
            Order → Quotation (where line-level <em>cost</em> + <em>markup</em>{" "}
            were recorded). <strong>Expenses</strong> counted are non-billable
            expenses linked to the invoice.{" "}
            <strong>Net profit = Revenue − Cost − Linked expenses.</strong>
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-1.5 text-xs mb-2 mr-2">
            <input
              data-testid="proj-only-with-cost"
              type="checkbox"
              checked={onlyWithCost}
              onChange={(e) => setOnlyWithCost(e.target.checked)}
              className="size-3.5"
            />
            Only projects with cost data
          </label>
          <div className="inline-flex border border-border rounded-md overflow-hidden mb-2">
            <button
              type="button"
              data-testid="proj-group-invoice"
              className={`px-2.5 py-1 text-xs ${groupBy === "invoice" ? "bg-primary text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              onClick={() => setGroupBy("invoice")}
            >
              By invoice
            </button>
            <button
              type="button"
              data-testid="proj-group-client"
              className={`px-2.5 py-1 text-xs border-l border-border ${groupBy === "client" ? "bg-primary text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              onClick={() => setGroupBy("client")}
            >
              By client
            </button>
          </div>
          <YearPicker year={year} setYear={setYear} testId="proj-year" />
          <Button
            data-testid="proj-pdf"
            variant="outline"
            size="sm"
            onClick={() => api.exportReportPdf({
              title: `Project Profitability — ${year}`,
              subtitle: `Grouped by ${groupBy} · Currency: ${currency}`,
              columns: ["Invoice", "Date", "Client", "Revenue", "Cost", "Expenses", "Net profit", "Margin %"],
              rows: rows.map((r) => [r.invoice_number || r.client_name, r.issue_date || "", r.client_name || "", fmtMoney(r.revenue), fmtMoney(r.cogs), fmtMoney((r.linked_expenses || 0) + (r.billable_expenses || 0)), fmtMoney(r.net_profit), r.margin_pct]),
            }, `project-profitability-${year}-${currency}.pdf`)}
          >
            <FileDown className="size-3.5 mr-1" /> PDF
          </Button>
          <Button
            data-testid="proj-csv"
            variant="outline"
            size="sm"
            onClick={() =>
              downloadCSV(`project-profitability-${year}-${currency}.csv`,
                rows.map((r) => ({
                  Invoice: r.invoice_number,
                  Date: r.issue_date,
                  Client: r.client_name,
                  "Project ref": r.project_ref,
                  Quotation: r.quotation_number,
                  Revenue: r.revenue,
                  Cost: r.cogs,
                  "Linked expenses": r.linked_expenses,
                  "Billable expenses": r.billable_expenses,
                  "Gross margin": r.gross_margin,
                  "Net profit": r.net_profit,
                  "Margin %": r.margin_pct,
                  "Has cost data": r.has_cost_data ? "yes" : "no",
                })))
            }
          >
            <Download className="size-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* KPI ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <PnlKpi label="Revenue" value={totals.revenue} currency={currency} tone="emerald" testid="proj-kpi-revenue" />
        <PnlKpi label="Cost (from markup)" value={totals.cogs} currency={currency} tone="slate" testid="proj-kpi-cogs" />
        <PnlKpi label="Linked expenses" value={totals.linked_expenses} currency={currency} tone="red" testid="proj-kpi-exp" />
        <PnlKpi label="Gross margin" value={totals.gross_margin} currency={currency} tone={totals.gross_margin >= 0 ? "emerald" : "red"} testid="proj-kpi-gm" />
        <PnlKpi
          label={`Net profit (${totals.margin_pct || 0}%)`}
          value={totals.net_profit}
          currency={currency}
          tone={totals.net_profit >= 0 ? "emerald" : "red"}
          testid="proj-kpi-net"
        />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">
          No projects to show for {year}.
        </p>
      ) : groupBy === "client" ? (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline">Client</TableHead>
                <TableHead className="overline text-right">Invoices</TableHead>
                <TableHead className="overline text-right">Revenue</TableHead>
                <TableHead className="overline text-right">Cost</TableHead>
                <TableHead className="overline text-right">Expenses</TableHead>
                <TableHead className="overline text-right">Gross margin</TableHead>
                <TableHead className="overline text-right">Net profit</TableHead>
                <TableHead className="overline text-right">Margin %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientRows.map((c) => (
                <TableRow key={c.client_name} data-testid={`proj-client-${c.client_name}`}>
                  <TableCell className="font-medium">{c.client_name}</TableCell>
                  <TableCell className="text-right mono text-sm">{c.invoices}</TableCell>
                  <TableCell className="text-right mono text-sm">{fmtMoney(c.revenue)}</TableCell>
                  <TableCell className="text-right mono text-sm">
                    {c.has_cost_data_count > 0 ? fmtMoney(c.cogs) : <span className="text-[10px] text-muted-foreground italic">n/a</span>}
                  </TableCell>
                  <TableCell className="text-right mono text-sm text-red-700">
                    {c.linked_expenses > 0 ? `-${fmtMoney(c.linked_expenses)}` : "—"}
                  </TableCell>
                  <TableCell className={`text-right mono text-sm ${c.gross_margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {c.has_cost_data_count > 0 ? fmtMoney(c.gross_margin) : "—"}
                  </TableCell>
                  <TableCell className={`text-right mono text-sm font-semibold ${c.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {c.has_cost_data_count > 0 ? fmtMoney(c.net_profit) : "—"}
                  </TableCell>
                  <TableCell className={`text-right mono text-sm ${c.margin_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {c.has_cost_data_count > 0 ? `${c.margin_pct}%` : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline">Invoice</TableHead>
                <TableHead className="overline">Date</TableHead>
                <TableHead className="overline">Client</TableHead>
                <TableHead className="overline">Project / Quote</TableHead>
                <TableHead className="overline text-right">Revenue</TableHead>
                <TableHead className="overline text-right">Cost</TableHead>
                <TableHead className="overline text-right">Expenses</TableHead>
                <TableHead className="overline text-right">Net profit</TableHead>
                <TableHead className="overline text-right">Margin %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`proj-row-${r.id}`}>
                  <TableCell className="mono text-xs">
                    <a href={`/invoices/${r.id}`} className="text-primary hover:underline">
                      {r.invoice_number}
                    </a>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground mono">{r.issue_date || "—"}</TableCell>
                  <TableCell className="text-sm">{r.client_name || "—"}</TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{r.project_ref || "—"}</div>
                    {r.quotation_number && (
                      <div className="text-[10px] text-muted-foreground mono">{r.quotation_number}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right mono text-sm">{fmtMoney(r.revenue)}</TableCell>
                  <TableCell className="text-right mono text-sm">
                    {r.has_cost_data
                      ? fmtMoney(r.cogs)
                      : <span className="text-[10px] text-muted-foreground italic">n/a</span>}
                  </TableCell>
                  <TableCell className="text-right mono text-sm text-red-700">
                    {r.linked_expenses > 0 ? `-${fmtMoney(r.linked_expenses)}` : "—"}
                  </TableCell>
                  <TableCell className={`text-right mono text-sm font-semibold ${r.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {r.has_cost_data ? fmtMoney(r.net_profit) : <span className="text-[10px] text-muted-foreground italic">n/a</span>}
                  </TableCell>
                  <TableCell className={`text-right mono text-sm ${r.margin_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {r.has_cost_data ? `${r.margin_pct}%` : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


function YearlyReport() {
  const currency = useCurrency();
  const { data, isLoading } = useQuery({
    queryKey: ["report", "year", currency],
    queryFn: () => api.reportSalesByYear(5, currency),
  });
  const rows = data?.rows || [];
  return (
    <div data-testid="report-yearly">
      <div className="flex items-end justify-between mb-4">
        <p className="text-xs text-muted-foreground">Last 5 calendar years.</p>
        <div className="flex gap-2">
          <Button data-testid="report-year-pdf" variant="outline"
                  onClick={() => api.exportReportPdf({
                    title: "Yearly Sales — last 5 years",
                    subtitle: `Currency: ${currency}`,
                    columns: ["Year", "Invoices", "Total", "Paid", "Outstanding"],
                    rows: rows.map((r) => [r.label, r.invoices, fmtMoney(r.total), fmtMoney(r.paid), fmtMoney(r.outstanding)]),
                  }, "sales-yearly.pdf")}>
            <FileDown className="size-3.5 mr-1.5" /> PDF
          </Button>
          <Button data-testid="report-year-csv" variant="outline"
                  onClick={() => downloadCSV("sales-yearly.csv", rows)}>
            <Download className="size-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>
      <div className="border border-border rounded-md bg-white p-3 mb-4" style={{ height: 320 }}>
        {isLoading ? <Skeleton /> : (
          <ResponsiveContainer>
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Line dataKey="total" name="Total" stroke={CHART_COLORS.total} strokeWidth={2} />
              <Line dataKey="paid" name="Paid" stroke={CHART_COLORS.paid} strokeWidth={2} />
              <Line dataKey="outstanding" name="Outstanding" stroke={CHART_COLORS.outstanding} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      <ReportTable
        headers={["Year", "Invoices", "Total", "Paid", "Outstanding"]}
        rows={rows.map((r) => [r.label, r.invoices, fmtMoney(r.total), fmtMoney(r.paid), fmtMoney(r.outstanding)])}
        testId="report-year-table"
      />
    </div>
  );
}


function ExpensesReport() {
  const currency = useCurrency();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, isLoading } = useQuery({
    queryKey: ["report", "expenses", year, currency],
    queryFn: () => api.reportExpensesSummary(year, currency),
  });
  const monthly = data?.monthly || [];
  const quarterly = data?.quarterly || [];
  const yearly = data?.yearly || [];
  const byCat = data?.by_category || [];
  const totals = data?.totals || { billable: 0, overhead: 0, total: 0, count: 0 };

  return (
    <div data-testid="report-expenses">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-xs text-muted-foreground">
            Multi-timeframe expenses breakdown — monthly, quarterly, and yearly rollups
            plus category totals. Currency-converted.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <YearPicker year={year} setYear={setYear} testId="exp-year" />
          <Button data-testid="exp-pdf" variant="outline" size="sm"
            onClick={() => api.exportReportPdf({
              title: `Expenses Summary — ${year}`,
              subtitle: `Currency: ${currency}`,
              columns: ["Month", "Overhead", "Billable", "Total"],
              rows: monthly.map((m) => [m.label, fmtMoney(m.overhead), fmtMoney(m.billable), fmtMoney(m.total)]),
            }, `expenses-${year}-${currency}.pdf`)}>
            <FileDown className="size-3.5 mr-1" /> PDF
          </Button>
          <Button data-testid="exp-csv" variant="outline" size="sm"
            onClick={() => downloadCSV(`expenses-${year}-${currency}.csv`,
              monthly.map((m) => ({ Month: m.label, Overhead: m.overhead, Billable: m.billable, Total: m.total })))}>
            <Download className="size-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* KPI ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <PnlKpi testid="exp-kpi-total" label="Total this year" value={totals.total} currency={currency} tone="slate" />
        <PnlKpi testid="exp-kpi-overhead" label="Overhead" value={totals.overhead} currency={currency} tone="red" />
        <PnlKpi testid="exp-kpi-billable" label="Billable" value={totals.billable} currency={currency} tone="emerald" />
        <PnlKpi testid="exp-kpi-count" label="Entries" value={totals.count} currency=""
                valueLabel={undefined} tone="slate" />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Monthly chart */}
          <div className="border border-border rounded-md bg-white p-4" data-testid="exp-monthly-chart">
            <p className="overline mb-2">Monthly — {year}</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthly} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                <Tooltip formatter={(v, n) => [`${fmtMoney(v)} ${currency}`, n]} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="overhead" name="Overhead" stackId="a" fill="#C8102E" radius={[2, 2, 0, 0]} />
                <Bar dataKey="billable" name="Billable" stackId="a" fill="#F59E0B" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Quarterly + Yearly */}
          <div className="grid grid-rows-2 gap-4">
            <div className="border border-border rounded-md bg-white p-4" data-testid="exp-quarterly-chart">
              <p className="overline mb-2">Quarterly — {year}</p>
              <ResponsiveContainer width="100%" height={110}>
                <BarChart data={quarterly} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <Tooltip formatter={(v, n) => [`${fmtMoney(v)} ${currency}`, n]} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="overhead" name="Overhead" stackId="a" fill="#C8102E" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="billable" name="Billable" stackId="a" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-border rounded-md bg-white p-4" data-testid="exp-yearly-chart">
              <p className="overline mb-2">Yearly (last 3)</p>
              <ResponsiveContainer width="100%" height={110}>
                <BarChart data={yearly} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <Tooltip formatter={(v, n) => [`${fmtMoney(v)} ${currency}`, n]} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="overhead" name="Overhead" stackId="a" fill="#C8102E" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="billable" name="Billable" stackId="a" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Category breakdown */}
      <div className="border border-border rounded-md bg-white overflow-x-auto mb-4" data-testid="exp-category-table">
        <div className="px-4 pt-3 pb-1"><p className="overline">By category — {year}</p></div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="overline">Category</TableHead>
              <TableHead className="overline text-right">Total</TableHead>
              <TableHead className="overline text-right">% of year</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {byCat.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">No expenses this year.</TableCell></TableRow>
            ) : (
              byCat.map((r) => {
                const pct = totals.total > 0 ? ((r.total / totals.total) * 100).toFixed(1) : "0.0";
                return (
                  <TableRow key={r.category} data-testid={`exp-cat-${r.category.replace(/\s+/g, "-").toLowerCase()}`}>
                    <TableCell className="font-medium">{r.category}</TableCell>
                    <TableCell className="text-right mono">{fmtMoney(r.total)} {currency}</TableCell>
                    <TableCell className="text-right mono text-muted-foreground">{pct}%</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Monthly rows table */}
      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <div className="px-4 pt-3 pb-1"><p className="overline">Monthly detail — {year}</p></div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-border">
              <TableHead className="overline">Month</TableHead>
              <TableHead className="overline text-right">Overhead</TableHead>
              <TableHead className="overline text-right">Billable</TableHead>
              <TableHead className="overline text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {monthly.map((r) => (
              <TableRow key={r.month} data-testid={`exp-month-${r.month}`}>
                <TableCell className="font-medium">{r.label}</TableCell>
                <TableCell className="text-right mono text-red-700">{fmtMoney(r.overhead)}</TableCell>
                <TableCell className="text-right mono text-amber-700">{fmtMoney(r.billable)}</TableCell>
                <TableCell className="text-right mono font-semibold">{fmtMoney(r.total)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-slate-50 font-semibold" data-testid="exp-month-total">
              <TableCell>Total {year}</TableCell>
              <TableCell className="text-right mono text-red-800">{fmtMoney(totals.overhead)}</TableCell>
              <TableCell className="text-right mono text-amber-800">{fmtMoney(totals.billable)}</TableCell>
              <TableCell className="text-right mono">{fmtMoney(totals.total)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}



function TopClientsReport() {
  const [limit, setLimit] = useState(10);
  const currency = useCurrency();
  const { data, isLoading } = useQuery({
    queryKey: ["report", "top-clients", limit, currency],
    queryFn: () => api.reportTopClients(limit, currency),
  });
  const rows = data?.rows || [];
  return (
    <div data-testid="report-top-clients">
      <div className="flex items-end justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <Label className="overline mb-0">Top</Label>
          <Input
            data-testid="report-top-clients-limit"
            type="number" min={3} max={50}
            value={limit} onChange={(e) => setLimit(Number(e.target.value || 10))}
            className="w-20"
          />
        </div>
        <Button data-testid="report-top-clients-csv" variant="outline"
                onClick={() => downloadCSV("top-clients.csv", rows)}>
          <Download className="size-3.5 mr-1.5" /> CSV
        </Button>
      </div>
      <div className="border border-border rounded-md bg-white p-3 mb-4" style={{ height: 320 }}>
        {isLoading ? <Skeleton /> : (
          <ResponsiveContainer>
            <BarChart data={rows} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" fontSize={11} />
              <YAxis dataKey="client_name" type="category" fontSize={11} width={130} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="paid" name="Paid" fill={CHART_COLORS.paid} />
              <Bar dataKey="outstanding" name="Outstanding" fill={CHART_COLORS.outstanding} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <ReportTable
        headers={["Client", "Invoices", "Total billed", "Paid", "Outstanding"]}
        rows={rows.map((r) => [r.client_name, r.invoices, fmtMoney(r.total), fmtMoney(r.paid), fmtMoney(r.outstanding)])}
        testId="report-top-clients-table"
      />
    </div>
  );
}


function OutstandingReport({ initialView = "invoice" }) {
  const currency = useCurrency();
  const [view, setView] = useState(initialView);
  const { data, isLoading } = useQuery({
    queryKey: ["report", "outstanding", currency],
    queryFn: () => api.reportOutstanding(currency),
  });
  const rows = data?.rows || [];
  const total = rows.reduce((s, r) => s + r.balance_due, 0);
  const overdueTotal = rows.filter((r) => r.overdue_days > 0).reduce((s, r) => s + r.balance_due, 0);

  const byClient = (() => {
    const m = new Map();
    for (const r of rows) {
      const key = r.client_id || r.client_name;
      const c = m.get(key) || {
        client_id: r.client_id, client_name: r.client_name || "Unknown",
        invoices: 0, overdue_invoices: 0, balance: 0, overdue_balance: 0, max_overdue: 0,
      };
      c.invoices += 1; c.balance += r.balance_due;
      if (r.overdue_days > 0) {
        c.overdue_invoices += 1; c.overdue_balance += r.balance_due;
        c.max_overdue = Math.max(c.max_overdue, r.overdue_days);
      }
      m.set(key, c);
    }
    return [...m.values()].sort((a, b) => b.overdue_balance - a.overdue_balance || b.balance - a.balance);
  })();

  return (
    <div data-testid="report-outstanding">
      <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
        <div className="flex gap-3">
          <KpiCard label="Total outstanding" value={`${fmtMoney(total)} ${currency}`} color="text-rose-700" />
          <KpiCard label="Of which overdue" value={`${fmtMoney(overdueTotal)} ${currency}`} color="text-red-700" />
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
            {[["invoice", "By invoice"], ["client", "By client"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`outstanding-view-${v}`}
                      onClick={() => setView(v)}
                      className={`px-3 py-1.5 ${view === v ? "bg-slate-800 text-white" : "bg-white hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
          <Button data-testid="report-outstanding-csv" variant="outline"
                  onClick={() => downloadCSV("outstanding-balances.csv", view === "client" ? byClient : rows)}>
            <Download className="size-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>
      {isLoading ? <Skeleton /> : view === "client" ? (
        byClient.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No outstanding balances.</p>
        ) : (
          <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="report-outstanding-clients">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border">
                  {["#", "Client", "Invoices", "Overdue inv.", "Oldest overdue", "Overdue balance", "Total balance"].map((h) => (
                    <TableHead key={h} className="overline">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {byClient.map((c, i) => (
                  <TableRow key={c.client_id || c.client_name}
                            data-testid={`outstanding-client-row-${c.client_id || i}`}
                            data-highlighted={i === 0 && c.overdue_balance > 0 ? "true" : undefined}
                            className={i === 0 && c.overdue_balance > 0 ? "bg-red-50 ring-2 ring-inset ring-red-400" : ""}>
                    <TableCell className="mono text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium">
                      {c.client_id
                        ? <Link to={`/clients/${c.client_id}`} className="text-primary hover:underline">{c.client_name}</Link>
                        : c.client_name}
                      {i === 0 && c.overdue_balance > 0 && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-600 text-white">WORST</span>
                      )}
                    </TableCell>
                    <TableCell className="mono text-sm">{c.invoices}</TableCell>
                    <TableCell className="mono text-sm">{c.overdue_invoices || "—"}</TableCell>
                    <TableCell className="mono text-sm">
                      {c.max_overdue ? <span className="text-rose-600 font-medium">{c.max_overdue}d</span> : "—"}
                    </TableCell>
                    <TableCell className="mono text-sm font-bold text-red-700">{fmtMoney(c.overdue_balance)}</TableCell>
                    <TableCell className="mono text-sm font-semibold">{fmtMoney(c.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : (
        <ReportTable
          headers={["Invoice", "Client", "Issue", "Due", "Total", "Paid", "Balance", "Overdue"]}
          rows={rows.map((r) => [
            r.invoice_number,
            r.client_name,
            fmtDate(r.issue_date),
            fmtDate(r.due_date),
            fmtMoney(r.total),
            fmtMoney(r.paid),
            <span key="bal" className="font-bold text-rose-700">{fmtMoney(r.balance_due)}</span>,
            r.overdue_days > 0
              ? <span key="ov" className="text-rose-600 font-medium">{r.overdue_days}d</span>
              : "—",
          ])}
          testId="report-outstanding-table"
        />
      )}
    </div>
  );
}


function SellingItemsReport() {
  const currency = useCurrency();
  const { data, isLoading } = useQuery({
    queryKey: ["report", "selling-items", currency],
    queryFn: () => api.reportSellingItems({ limit: 20, currency }),
  });
  const rows = data?.rows || [];
  return (
    <div data-testid="report-selling-items">
      <div className="flex items-end justify-between mb-4 gap-3">
        <p className="text-xs text-muted-foreground">Top 20 items / charges across all invoices.</p>
        <Button data-testid="report-selling-items-csv" variant="outline"
                onClick={() => downloadCSV("selling-items.csv", rows)}>
          <Download className="size-3.5 mr-1.5" /> CSV
        </Button>
      </div>
      <div className="border border-border rounded-md bg-white p-3 mb-4" style={{ height: 320 }}>
        {isLoading ? <Skeleton /> : (
          <ResponsiveContainer>
            <BarChart data={rows.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" fontSize={11} />
              <YAxis dataKey="name" type="category" fontSize={11} width={130} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="revenue" name="Revenue" fill={CHART_COLORS.total} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <ReportTable
        headers={["Item / Description", "Type", "Spec", "Qty sold", "Invoices", "Revenue"]}
        rows={rows.map((r) => [
          r.name,
          (r.line_type || "item").replace("_", " "),
          r.specification || "—",
          r.qty,
          r.invoices,
          fmtMoney(r.revenue),
        ])}
        testId="report-selling-items-table"
      />
    </div>
  );
}


function TaxReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const currency = useCurrency();
  const { data, isLoading } = useQuery({
    queryKey: ["report", "tax", year, currency],
    queryFn: () => api.reportTaxSummary(year, currency),
  });
  const rows = data?.rows || [];
  return (
    <div data-testid="report-tax">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <YearPicker year={year} setYear={setYear} testId="report-tax-year" />
        <div className="flex items-center gap-3">
          <KpiCard label="Tax collected" value={`${fmtMoney(data?.total_tax || 0)} ${currency}`} color="text-amber-800" />
          <KpiCard label="Taxable base" value={`${fmtMoney(data?.total_taxable || 0)} ${currency}`} color="text-primary" />
          <Button data-testid="report-tax-csv" variant="outline"
                  onClick={() => downloadCSV(`tax-summary-${year}.csv`, rows)}>
            <Download className="size-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>
      <div className="border border-border rounded-md bg-white p-3 mb-4" style={{ height: 320 }}>
        {isLoading ? <Skeleton /> : (
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="taxable" name="Taxable" fill={CHART_COLORS.total} />
              <Bar dataKey="tax" name="Tax" fill={CHART_COLORS.tax} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <ReportTable
        headers={["Month", "Taxable base", "Tax"]}
        rows={rows.map((r) => [r.label, fmtMoney(r.taxable), fmtMoney(r.tax)])}
        testId="report-tax-table"
      />
    </div>
  );
}


// ───────── small presentational helpers ─────────

function KpiCard({ label, value, color }) {
  return (
    <div className="border border-border rounded-md bg-white p-4">
      <p className="overline">{label}</p>
      <p className={`mono font-bold text-lg mt-1 ${color}`}>{value}</p>
    </div>
  );
}

function Skeleton() {
  return <div className="h-full w-full animate-pulse bg-slate-100 rounded" />;
}

function ReportTable({ headers, rows, testId }) {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground py-6 text-center">No data for this range.</p>;
  }
  return (
    <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-b border-border">
            {headers.map((h) => (
              <TableHead key={h} className="overline">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((cols, i) => (
            <TableRow key={i}>
              {cols.map((c, j) => (
                <TableCell key={j} className={j === 0 ? "font-medium" : "mono text-sm"}>{c}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}



// ───────── Staff leaderboard ─────────

/**
 * Metric key → { label, actionKey, targetKey, tone }.
 * `actionKey` looks up the achieved count on the leaderboard entry;
 * `targetKey` looks it up on the targets doc.
 */
const LEADERBOARD_METRICS = [
  { key: "quotations", label: "Quotations", actionKey: "quotations_created", targetKey: "quotations_target", tone: "text-amber-800" },
  { key: "conversions", label: "Converted", actionKey: "quotations_converted", targetKey: "conversions_target", tone: "text-indigo-700" },
  { key: "invoices", label: "Invoices", actionKey: "invoices_created", targetKey: "invoices_target", tone: "text-emerald-700" },
  { key: "purchase_orders", label: "POs", actionKey: "purchase_orders_created", targetKey: "purchase_orders_target", tone: "text-sky-700" },
  { key: "site_hours", label: "Site hours", actionKey: null, targetKey: "attendance_hours_target", tone: "text-red-700" },
];

function TargetedStat({ label, value, target, tone }) {
  const hasTarget = target > 0;
  const numeric = Number(value) || 0;
  const pct = hasTarget ? Math.min(999, Math.round((numeric / target) * 100)) : null;
  let deltaClass = "text-muted-foreground";
  let deltaLabel = null;
  if (hasTarget) {
    if (pct >= 100) {
      deltaClass = "text-emerald-700 bg-emerald-50 ring-emerald-200";
      deltaLabel = `${pct}% ✓`;
    } else if (pct >= 66) {
      deltaClass = "text-amber-800 bg-amber-50 ring-amber-200";
      deltaLabel = `${pct}%`;
    } else {
      deltaClass = "text-red-700 bg-red-50 ring-red-200";
      deltaLabel = `${pct}%`;
    }
  }
  const barPct = hasTarget ? Math.min(100, Math.round((numeric / target) * 100)) : 0;
  const barColor =
    !hasTarget ? "bg-slate-200" :
    pct >= 100 ? "bg-emerald-500" :
    pct >= 66 ? "bg-amber-500" :
    "bg-red-500";
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex items-baseline gap-1.5">
        <p className={`mono font-bold ${tone}`}>{value}</p>
        {hasTarget && (
          <>
            <p className="text-[10px] text-muted-foreground mono">/ {target}</p>
            <span
              className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset ${deltaClass}`}
            >
              {deltaLabel}
            </span>
          </>
        )}
      </div>
      {hasTarget && (
        <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${barPct}%` }} />
        </div>
      )}
    </div>
  );
}

const emptyTargetForm = {
  quotations_target: 0,
  conversions_target: 0,
  invoices_target: 0,
  purchase_orders_target: 0,
  attendance_hours_target: 0,
};

function EditTargetsDialog({ open, onOpenChange, staffMember }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyTargetForm);

  useEffect(() => {
    if (!staffMember) return;
    const t = staffMember.targets || {};
    setForm({
      quotations_target: t.quotations_target || 0,
      conversions_target: t.conversions_target || 0,
      invoices_target: t.invoices_target || 0,
      purchase_orders_target: t.purchase_orders_target || 0,
      attendance_hours_target: t.attendance_hours_target || 0,
    });
  }, [staffMember]);

  const saveMut = useMutation({
    mutationFn: (body) => api.setStaffTarget(staffMember.user_id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff-leaderboard"] });
      toast.success(`Targets updated for ${staffMember.name}`);
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const clearMut = useMutation({
    mutationFn: () => api.deleteStaffTarget(staffMember.user_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff-leaderboard"] });
      toast.success(`Cleared targets for ${staffMember.name}`);
      onOpenChange(false);
    },
  });

  if (!staffMember) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-targets-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Monthly targets — {staffMember.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Set the monthly goals for this staff member. 0 means &ldquo;no target&rdquo; and
            hides the progress badge for that metric.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[
            { k: "quotations_target", label: "Quotations created" },
            { k: "conversions_target", label: "Quotations converted" },
            { k: "invoices_target", label: "Invoices created" },
            { k: "purchase_orders_target", label: "Purchase orders" },
            { k: "attendance_hours_target", label: "Site hours" },
          ].map(({ k, label }) => (
            <div key={k}>
              <Label className="overline">{label}</Label>
              <Input
                data-testid={`target-${k}`}
                type="number"
                min="0"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              />
            </div>
          ))}
        </div>
        <DialogFooter className="flex-wrap">
          <Button
            variant="ghost"
            className="text-red-600 mr-auto"
            data-testid="target-clear-btn"
            onClick={() => clearMut.mutate()}
            disabled={!staffMember.targets || clearMut.isPending}
          >
            Clear all targets
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="target-cancel">
            Cancel
          </Button>
          <Button
            data-testid="target-save"
            className="bg-primary hover:bg-primary/90"
            onClick={() => saveMut.mutate(form)}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save targets"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffLeaderboardReport() {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState("week");
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["staff-leaderboard", period],
    queryFn: () => api.staffLeaderboard(period),
    refetchOnWindowFocus: false,
  });
  const staff = data?.staff || [];
  const currency = useCurrency();

  const topScore = staff[0]?.score || 1;
  const periodLabel = { week: "Last 7 days", month: "This month", today: "Today" }[period];

  return (
    <div data-testid="staff-leaderboard-report">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="overline">Staff productivity</p>
          <p className="text-sm text-muted-foreground">
            {periodLabel} · {staff.length} staff active
            {period === "month" && " · target progress shown"}
          </p>
        </div>
        <div className="inline-flex rounded-full bg-slate-100 p-0.5 text-xs">
          {[
            { id: "today", label: "Today" },
            { id: "week", label: "Week" },
            { id: "month", label: "Month" },
          ].map((p) => (
            <button
              key={p.id}
              data-testid={`leaderboard-period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1 rounded-full transition ${
                period === p.id
                  ? "bg-red-600 text-white font-semibold shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : staff.length === 0 ? (
        <div className="border border-border rounded-md bg-white p-8 text-center text-sm text-muted-foreground italic">
          No staff activity recorded {periodLabel.toLowerCase()}. As soon as your team
          starts logging quotations, POs and site visits, they&apos;ll appear here.
        </div>
      ) : (
        <div className="space-y-3">
          {staff.map((s, i) => {
            const barPct = Math.max(4, Math.round((s.score / topScore) * 100));
            const medal =
              i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
            const targets = s.targets || {};
            // Show progress badges only when we're looking at a monthly window.
            const showTargets = period === "month";
            return (
              <div
                key={s.user_id}
                data-testid={`leaderboard-row-${s.user_id}`}
                className={`border rounded-md bg-white overflow-hidden ${
                  i === 0 ? "border-red-200" : "border-border"
                }`}
              >
                <div className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="text-xl leading-none min-w-[36px] text-center">
                    {medal}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-semibold text-foreground">{s.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {s.role}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button
                      data-testid={`leaderboard-edit-targets-${s.user_id}`}
                      variant="ghost"
                      size="sm"
                      className="text-primary"
                      onClick={() => setEditing(s)}
                    >
                      <Target className="size-3.5 mr-1" /> Targets
                    </Button>
                  )}
                  <div className="text-right">
                    <p className="mono text-2xl font-bold text-primary">{s.score}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Score
                    </p>
                  </div>
                </div>
                <div className="px-4 pb-2">
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${i === 0 ? "bg-red-600" : "bg-slate-500"}`}
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-3 px-4 pb-4">
                  {LEADERBOARD_METRICS.map((m) => {
                    const val =
                      m.key === "site_hours"
                        ? s.attendance_hours.toFixed(1)
                        : s.actions[m.actionKey];
                    return (
                      <TargetedStat
                        key={m.key}
                        label={m.label}
                        value={val}
                        target={showTargets ? targets[m.targetKey] || 0 : 0}
                        tone={m.tone}
                      />
                    );
                  })}
                </div>
                {s.totals.revenue_created > 0 && (
                  <div className="px-4 pb-3 text-[11px] text-muted-foreground border-t border-border pt-2">
                    Revenue on newly created invoices+quotations:{" "}
                    <span className="mono font-semibold text-foreground">
                      {fmtMoney(s.totals.revenue_created)} {currency}
                    </span>
                    {s.totals.po_committed > 0 && (
                      <>
                        {" · "}PO committed:{" "}
                        <span className="mono font-semibold text-foreground">
                          {fmtMoney(s.totals.po_committed)} {currency}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-4 italic">
        Score weighting: quotation conversions & invoice sent = highest, PO received & new docs = strong, edits/status = light,
        client-facing attendance = 2 pts per hour. Target progress is shown on the Month view.
      </p>

      <EditTargetsDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        staffMember={editing}
      />
    </div>
  );
}

// Kept for backwards-compat with older code paths.
function Stat({ label, value, tone }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mono font-bold ${tone}`}>{value}</p>
    </div>
  );
}

// ───────── Page entry ─────────

export default function Reports() {
  const { t } = useI18n();
  const currency = useCurrency();
  const { data: fx } = useQuery({ queryKey: ["fx"], queryFn: api.fxRates });
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "monthly";
  const outstandingView = searchParams.get("view") === "client" ? "client" : "invoice";

  return (
    <div data-testid="reports-page">
      <PageHeader
        overline="Reports"
        title="Sales reports"
        subtitle={<span className="text-muted-foreground text-sm">Monthly · quarterly · yearly breakdowns, plus top clients, outstanding balances, top-selling items and tax summary.</span>}
        actions={
          <div className="flex items-center gap-2">
            <Label className="overline mb-0">Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="report-currency-select" className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fx?.rates && currency !== "USD" && (
              <span data-testid="report-fx-info" className="text-[10px] text-muted-foreground mono ml-1">
                1 USD = {Number(fx.rates[currency]).toFixed(currency === "IDR" ? 0 : 4)} {currency}
              </span>
            )}
          </div>
        }
      />
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="flex flex-wrap gap-1 h-auto mb-4">
          <TabsTrigger data-testid="tab-monthly" value="monthly">Monthly</TabsTrigger>
          <TabsTrigger data-testid="tab-quarterly" value="quarterly">Quarterly</TabsTrigger>
          <TabsTrigger data-testid="tab-yearly" value="yearly">Yearly</TabsTrigger>
          <TabsTrigger data-testid="tab-pnl" value="pnl">Profit &amp; loss</TabsTrigger>
          <TabsTrigger data-testid="tab-project-profit" value="project-profit">Project profit</TabsTrigger>
          <TabsTrigger data-testid="tab-expenses" value="expenses">Expenses</TabsTrigger>
          <TabsTrigger data-testid="tab-top-clients" value="top">Top clients</TabsTrigger>
          <TabsTrigger data-testid="tab-outstanding" value="outstanding">Outstanding</TabsTrigger>
          <TabsTrigger data-testid="tab-selling-items" value="items">Selling items</TabsTrigger>
          <TabsTrigger data-testid="tab-tax" value="tax">Tax</TabsTrigger>
          <TabsTrigger data-testid="tab-leaderboard" value="leaderboard">Staff leaderboard</TabsTrigger>
          <TabsTrigger data-testid="tab-supplier-bids" value="supplier-bids">Supplier bids</TabsTrigger>
        </TabsList>
        <TabsContent value="monthly"><MonthlyReport /></TabsContent>
        <TabsContent value="quarterly"><QuarterlyReport /></TabsContent>
        <TabsContent value="yearly"><YearlyReport /></TabsContent>
        <TabsContent value="pnl"><ProfitLossReport /></TabsContent>
        <TabsContent value="project-profit"><ProjectProfitabilityReport /></TabsContent>
        <TabsContent value="expenses"><ExpensesReport /></TabsContent>
        <TabsContent value="top"><TopClientsReport /></TabsContent>
        <TabsContent value="outstanding"><OutstandingReport initialView={outstandingView} /></TabsContent>
        <TabsContent value="items"><SellingItemsReport /></TabsContent>
        <TabsContent value="tax"><TaxReport /></TabsContent>
        <TabsContent value="leaderboard"><StaffLeaderboardReport /></TabsContent>
        <TabsContent value="supplier-bids"><SupplierBidsReport /></TabsContent>
      </Tabs>
    </div>
  );
}

function SupplierBidsReport() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [winnersOnly, setWinnersOnly] = useState(false);
  const filterParams = {};
  if (dateFrom) filterParams.from = dateFrom;
  if (dateTo) filterParams.to = dateTo;
  if (winnersOnly) filterParams.winners_only = 1;

  const { data, isLoading } = useQuery({
    queryKey: ["rfq-supplier-analytics", dateFrom, dateTo, winnersOnly],
    queryFn: () => api.rfqSupplierAnalytics(filterParams),
  });
  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  const suppliers = (data && data.suppliers) || [];
  const hasFilter = Boolean(dateFrom || dateTo || winnersOnly);

  const FilterBar = (
    <div
      data-testid="supplier-bids-filter-bar"
      className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-slate-50 p-3"
    >
      <div>
        <Label className="overline">From</Label>
        <div className="w-40">
          <DatePicker
            testId="supplier-bids-filter-from"
            value={dateFrom}
            onChange={(v) => setDateFrom(v || "")}
            placeholder="Any date"
          />
        </div>
      </div>
      <div>
        <Label className="overline">To</Label>
        <div className="w-40">
          <DatePicker
            testId="supplier-bids-filter-to"
            value={dateTo}
            onChange={(v) => setDateTo(v || "")}
            placeholder="Any date"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 h-8 pl-2 text-xs text-muted-foreground">
        <input
          data-testid="supplier-bids-filter-winners"
          type="checkbox"
          checked={winnersOnly}
          onChange={(e) => setWinnersOnly(e.target.checked)}
          className="size-3.5"
        />
        Winning bids only
      </label>
      {hasFilter && (
        <Button
          data-testid="supplier-bids-filter-reset"
          variant="ghost"
          size="sm"
          onClick={() => { setDateFrom(""); setDateTo(""); setWinnersOnly(false); }}
        >
          Reset
        </Button>
      )}
      <div className="flex-1" />
      <Button
        data-testid="supplier-bids-export-csv"
        variant="outline"
        size="sm"
        onClick={async () => {
          try {
            await api.downloadRfqSupplierAnalyticsCsv(filterParams);
            toast.success("Supplier bids CSV downloaded");
          } catch {
            toast.error("Could not download CSV");
          }
        }}
      >
        <Download className="size-3.5 mr-1.5" />
        Download CSV
      </Button>
    </div>
  );

  if (suppliers.length === 0) {
    return (
      <div className="space-y-4">
        {FilterBar}
        <div
          data-testid="supplier-bids-empty"
          className="border border-dashed border-border rounded-md p-6 text-center text-sm text-muted-foreground"
        >
          {hasFilter
            ? "No supplier bids match those filters. Try widening the date range or turning off Winners only."
            : "No supplier bids recorded yet. Create RFQs, invite suppliers, then record quotes to see analytics."}
        </div>
      </div>
    );
  }
  const maxBids = Math.max(...suppliers.map((s) => s.bids), 1);
  return (
    <div data-testid="supplier-bids-report" className="space-y-4">
      {FilterBar}
      <p className="text-xs text-muted-foreground">
        Aggregated from every quote across {data.total_rfqs_with_bids} RFQ
        {data.total_rfqs_with_bids === 1 ? "" : "s"} · sorted by win rate.
      </p>
      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="overline text-left px-3 py-2">Supplier</th>
              <th className="overline text-right px-3 py-2">Bids</th>
              <th className="overline text-right px-3 py-2">Wins</th>
              <th className="overline text-right px-3 py-2">Win rate</th>
              <th className="overline text-right px-3 py-2">Avg lead time</th>
              <th className="overline text-right px-3 py-2">Avg savings vs target</th>
              <th className="overline text-left px-3 py-2">Last bid</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => {
              const winPct = Math.round((s.win_rate || 0) * 100);
              const savingsPct = s.avg_savings_pct == null ? null
                : Math.round(s.avg_savings_pct * 1000) / 10;
              return (
                <tr
                  key={s.supplier_name}
                  data-testid={`supplier-bid-row-${(s.supplier_name || "").replace(/\s+/g, "-").toLowerCase()}`}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-3 py-2 font-medium">{s.supplier_name}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      <span className="mono">{s.bids}</span>
                      <div className="hidden sm:block w-16 h-1.5 bg-slate-100 rounded">
                        <div
                          className="h-full bg-primary rounded"
                          style={{ width: `${(s.bids / maxBids) * 100}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right mono">{s.wins}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold ring-1 ring-inset ${
                      winPct >= 66 ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : winPct >= 33 ? "bg-amber-50 text-amber-700 ring-amber-200"
                        : "bg-slate-50 text-slate-700 ring-slate-200"
                    }`}>
                      {winPct}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right mono">
                    {s.avg_lead_time_days == null ? "—" : `${s.avg_lead_time_days}d`}
                  </td>
                  <td className="px-3 py-2 text-right mono">
                    {savingsPct == null
                      ? "—"
                      : <span className={savingsPct >= 0 ? "text-emerald-700" : "text-red-600"}>
                          {savingsPct >= 0 ? "+" : ""}{savingsPct}%
                        </span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {s.last_bid_at ? String(s.last_bid_at).slice(0, 10) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
